import React, { useEffect, useState } from 'react';
import { PencilIcon, TrashIcon, PlusIcon, CheckCircleIcon, EnvelopeOpenIcon, EnvelopeIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import API from '../api';
import Modal from './Modal';
import { slugify, sanitizeSlugInput } from '../utils/slug';

const EMPTY_JOB = { title: '', slug: '', code_number: '', location: '', employment_type: 'Full-time', description: '', requirements: '', sort_order: 0, is_active: 1 };
const BASE = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api', '') : 'http://localhost:5000';

function formatDate(str) {
  return str ? new Date(str).toLocaleString('en-MY', { dateStyle: 'medium', timeStyle: 'short' }) : '—';
}

function PromoSettingsForm() {
  const [settings, setSettings] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    API.get('/settings').then(r => setSettings(r.data)).catch(() => {});
  }, []);

  const imgSrc = (url) => url?.startsWith('/') ? `${BASE}${url}` : url;

  const handleUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await API.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSettings(s => ({ ...s, careers_image_url: data.url }));
    } catch { alert('Upload failed'); }
    setUploading(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await API.put('/settings', {
        careers_image_url: settings.careers_image_url || '',
        careers_intro_title: settings.careers_intro_title || '',
        careers_intro_content: settings.careers_intro_content || '',
        careers_intro_title_ja: settings.careers_intro_title_ja || '',
        careers_intro_content_ja: settings.careers_intro_content_ja || '',
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch { alert('Failed to save.'); }
    setSaving(false);
  };

  const field = (key, label, { textarea, rows, hint } = {}) => (
    <div>
      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{label}</label>
      {textarea ? (
        <textarea value={settings[key] || ''} onChange={e => setSettings(s => ({ ...s, [key]: e.target.value }))}
          rows={rows || 4} className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary resize-y" />
      ) : (
        <input value={settings[key] || ''} onChange={e => setSettings(s => ({ ...s, [key]: e.target.value }))}
          className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary" />
      )}
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );

  return (
    <form onSubmit={handleSave} className="bg-white border border-gray-100 shadow-sm p-6 space-y-5 mb-8">
      <div>
        <h2 className="font-semibold text-gray-700 text-sm uppercase tracking-wider mb-1">Intro Section</h2>
        <p className="text-xs text-gray-400">Shown at the top of the public Careers page.</p>
      </div>
      {field('careers_intro_title', 'Intro Title')}
      {field('careers_intro_content', 'Intro Content', { textarea: true, hint: 'Separate paragraphs with a blank line.' })}
      {field('careers_intro_title_ja', 'Intro Title (Japanese)', { hint: 'Falls back to the English title if left blank.' })}
      {field('careers_intro_content_ja', 'Intro Content (Japanese)', { textarea: true, hint: 'Falls back to the English content if left blank.' })}
      <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Image</label>
        {settings.careers_image_url && (
          <div className="h-32 bg-gray-50 border border-gray-200 overflow-hidden mb-2">
            <img src={imgSrc(settings.careers_image_url)} alt="preview" className="w-full h-full object-cover" />
          </div>
        )}
        <input type="file" accept="image/*" onChange={handleUpload} disabled={uploading}
          className="block w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-4 file:border-0 file:bg-primary file:text-white file:text-xs file:cursor-pointer mb-2" />
        <input value={settings.careers_image_url || ''} onChange={e => setSettings(s => ({ ...s, careers_image_url: e.target.value }))}
          placeholder="or paste image URL..." className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary" />
      </div>
      <div className="flex items-center gap-4 pt-2">
        <button type="submit" disabled={saving} className="btn-primary text-sm disabled:opacity-60">
          {saving ? 'Saving...' : 'Save Intro Section'}
        </button>
        {saved && (
          <span className="flex items-center gap-1.5 text-sm text-green-600">
            <CheckCircleIcon className="w-4 h-4" /> Saved!
          </span>
        )}
      </div>
    </form>
  );
}

function JobOpeningsManager() {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_JOB);
  const [saving, setSaving] = useState(false);

  const load = () => API.get('/careers/all').then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openAdd = () => {
    const nextOrder = items.length ? Math.max(...items.map(i => i.sort_order)) + 1 : 0;
    setForm({ ...EMPTY_JOB, sort_order: nextOrder });
    setModal({ mode: 'add' });
  };
  const openEdit = (item) => { setForm(item); setModal({ mode: 'edit', id: item.id }); };

  const handleTitleChange = (title) => {
    setForm(f => ({ ...f, title, slug: modal?.mode === 'add' ? slugify(title) : f.slug }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (modal.mode === 'add') await API.post('/careers', form);
      else await API.put(`/careers/${modal.id}`, form);
      setModal(null); load();
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this job opening? Any applications received for it will be deleted too.')) return;
    await API.delete(`/careers/${id}`); load();
  };

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold text-gray-700 text-sm uppercase tracking-wider">Job Openings</h2>
        <button onClick={openAdd} className="btn-primary text-sm flex items-center gap-2">
          <PlusIcon className="w-4 h-4" /> Add Job Opening
        </button>
      </div>

      <div className="bg-white border border-gray-100 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-left">
              {['Title', 'Code Number', 'Location', 'Type', 'Status', ''].map(h => (
                <th key={h} className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-400">No job openings yet. Click "Add Job Opening" to get started.</td></tr>
            )}
            {items.map(item => (
              <tr key={item.id}>
                <td className="px-4 py-3 font-medium text-gray-800">{item.title}</td>
                <td className="px-4 py-3 text-gray-500 font-mono text-xs">{item.code_number || '—'}</td>
                <td className="px-4 py-3 text-gray-500">{item.location || '—'}</td>
                <td className="px-4 py-3 text-gray-500">{item.employment_type || '—'}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-medium px-2 py-0.5 ${item.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {item.is_active ? 'Active' : 'Hidden'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(item)} className="p-1.5 text-gray-400 hover:text-primary border border-gray-200 hover:border-primary transition-colors">
                      <PencilIcon className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleDelete(item.id)} className="p-1.5 text-gray-400 hover:text-red-500 border border-gray-200 hover:border-red-300 transition-colors">
                      <TrashIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <Modal title={modal.mode === 'add' ? 'Add Job Opening' : 'Edit Job Opening'} onClose={() => setModal(null)} wide>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Title *</label>
              <input value={form.title} onChange={e => handleTitleChange(e.target.value)}
                className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Slug *</label>
                <input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: sanitizeSlugInput(e.target.value) }))}
                  className="w-full border border-gray-200 px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Code Number</label>
                <input value={form.code_number || ''} onChange={e => setForm(f => ({ ...f, code_number: e.target.value }))}
                  placeholder="e.g. ACCA-SRB2K24" className="w-full border border-gray-200 px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary" />
                <p className="text-xs text-gray-400 mt-1">A reference code shown to applicants — your own naming scheme.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Location</label>
                <input value={form.location || ''} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                  placeholder="e.g. Santo Tomas, Batangas" className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Employment Type</label>
                <select value={form.employment_type || ''} onChange={e => setForm(f => ({ ...f, employment_type: e.target.value }))}
                  className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary">
                  {['Full-time', 'Part-time', 'Contract', 'Internship'].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Job Overview</label>
              <textarea value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                rows={4} className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary resize-y" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Qualifications</label>
              <textarea value={form.requirements || ''} onChange={e => setForm(f => ({ ...f, requirements: e.target.value }))}
                rows={4} placeholder="One qualification per line" className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary resize-y" />
              <p className="text-xs text-gray-400 mt-1">One per line — shown as a bullet list.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Sort Order</label>
                <input type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: +e.target.value }))}
                  className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Status</label>
                <select value={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: +e.target.value }))}
                  className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary">
                  <option value={1}>Active</option>
                  <option value={0}>Hidden</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={handleSave} disabled={saving || !form.title || !form.slug} className="btn-primary text-sm disabled:opacity-60 flex-1">
                {saving ? 'Saving...' : 'Save Job Opening'}
              </button>
              <button onClick={() => setModal(null)} className="btn-outline text-sm flex-1">Cancel</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function ApplicationsManager() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => API.get('/careers/applications/all').then(r => setApps(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const markRead = async (app) => {
    if (app.is_read) return;
    await API.put(`/careers/applications/${app.id}/read`).catch(() => {});
    setApps(list => list.map(a => a.id === app.id ? { ...a, is_read: 1 } : a));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this application?')) return;
    await API.delete(`/careers/applications/${id}`); load();
  };

  const unread = apps.filter(a => !a.is_read).length;

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <h2 className="font-semibold text-gray-700 text-sm uppercase tracking-wider">Applications</h2>
        {unread > 0 && <span className="text-xs bg-accent text-white px-2 py-0.5 font-medium">{unread} unread</span>}
      </div>

      {loading ? (
        <div className="bg-white border border-gray-100 shadow-sm p-12 text-center text-gray-400">Loading...</div>
      ) : (
        <div className="bg-white border border-gray-100 shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left">
                {['', 'Applicant', 'Applied For', 'Contact', 'Received', 'Resume', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {apps.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-400">No applications yet.</td></tr>
              )}
              {apps.map(app => (
                <tr key={app.id} onClick={() => markRead(app)} className={`cursor-pointer hover:bg-gray-50 ${!app.is_read ? 'bg-accent/5' : ''}`}>
                  <td className="px-4 py-3">
                    {app.is_read
                      ? <EnvelopeOpenIcon className="w-4 h-4 text-gray-300" />
                      : <EnvelopeIcon className="w-4 h-4 text-accent" />}
                  </td>
                  <td className={`px-4 py-3 ${!app.is_read ? 'font-semibold text-gray-800' : 'text-gray-700'}`}>{app.first_name} {app.last_name}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {app.job_title}
                    {app.job_code_number && <span className="block text-xs text-gray-400 font-mono">{app.job_code_number}</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    <div>{app.email}</div>
                    {app.phone && <div>{app.phone}</div>}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{formatDate(app.created_at)}</td>
                  <td className="px-4 py-3">
                    {app.resume_url ? (
                      <a href={`${BASE}${app.resume_url}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                        className="flex items-center gap-1 text-primary hover:text-accent transition-colors text-xs font-medium">
                        <DocumentArrowDownIcon className="w-4 h-4" /> View
                      </a>
                    ) : <span className="text-gray-300 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={e => { e.stopPropagation(); handleDelete(app.id); }}
                      className="p-1.5 text-gray-400 hover:text-red-500 border border-gray-200 hover:border-red-300 transition-colors">
                      <TrashIcon className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AdminCareers() {
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-display font-bold text-primary-dark">Careers</h1>
        <p className="text-sm text-gray-500 mt-1">Manage the public Careers page, job openings, and applications</p>
      </div>
      <PromoSettingsForm />
      <JobOpeningsManager />
      <ApplicationsManager />
    </div>
  );
}
